import { FC, useMemo } from 'react'
import { AdvisorRequestPanePreset } from 'components/features/coaching/advisorMeeting/AdvisorRequestPanePreset/AdvisorRequestPanePreset'
import { useStoreSelector } from 'helpers/reactReduxHelpers'
import styles from 'components/features/coaching/advisorMeeting/AdvisorRequestConfirmed/AdvisorRequestConfirmed.styl'
import { PaneNote } from 'components/features/coaching/advisorMeeting/PaneNote/PaneNote'
import { createAdvisorSelector } from 'components/features/coaching/advisorMeeting/selectors/advisorsSelector'
import moment from 'moment'
import { AdvisorAvatar } from 'components/features/coaching/advisor/AdvisorAvatar/AdvisorAvatar'
import { MeetingUid, Meeting } from 'components/features/coaching/advisorPage/types/interfaces'
import { AdvisorUid } from 'components/features/coaching/advisor/types/advisor'
import { CardBlock } from 'components/features/pfm/Blocks/Blocks'
import { AdvisorMeetingWizardFields } from 'components/features/coaching/advisorMeeting/types/requests'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'

type AdvisorRequestConfirmedProps = {
  uid: MeetingUid
  fields: AdvisorMeetingWizardFields
  onClose: () => void
}
export const AdvisorRequestConfirmed: FC<AdvisorRequestConfirmedProps> = (props) => {
  const { onClose, uid, fields } = props

  const meeting = useStoreSelector((state) => {
    return state.data.advisors.meetings.data?.byKey[uid] ?? null
  })
  const advisorUid = meeting?.advisorUid ?? null

  if (!meeting || !advisorUid) {
    return null
  }

  return (
    <AdvisorRequestConfirmedInner
      meeting={ meeting }
      advisorUid={ advisorUid }
      uid={ uid }
      fields={ fields }
      onClose={ onClose }
    />
  )
}

type AdvisorRequestConfirmedInnerProps =
  & AdvisorRequestConfirmedProps
  & {
    meeting: Meeting
    advisorUid: AdvisorUid
  }
const AdvisorRequestConfirmedInner: FC<AdvisorRequestConfirmedInnerProps> = (props) => {
  const { onClose, meeting, advisorUid, fields } = props
  const { flow } = fields

  const { timeslot } = meeting

  const withAdvisorUid = useMemo(() => ({ uid: advisorUid }), [advisorUid])
  const advisorSelector = useMemo(createAdvisorSelector, [])
  const advisor = useStoreSelector((state) => advisorSelector(state, withAdvisorUid))

  const avatarChild = useMemo(() => (
    <div className={ styles.avatar }>
      <AdvisorAvatar uid={ advisorUid } />
    </div>
  ), [advisorUid])

  const dateChild = useMemo(() => moment(timeslot).format('MMM Do'), [timeslot])
  const timeChild = useMemo(() => moment(timeslot).format('h:mm A'), [timeslot])

  return (
    <AdvisorRequestPanePreset
      title='Check Your Email'
      actionName={ flow === 'regular' ? 'CLOSE' : 'COMPLETE YOUR PROFILE' }
      onCancel={ onClose }
      cancelName=''
      onSuccess={ onClose }
      iconSlug='check'
      progressBarPercent={ 100 }
    >
      <CardBlock
        corners='all'
        forceRoundedCorners
        shadowTheme='soft'
        noBorders
        className={ styles.card }
      >
        <PaneNote className={ styles.cardContent }>
          { avatarChild }
          Your session with { advisor?.name } <br />
          on <b>{ dateChild }</b> at <b>{ timeChild }</b> is confirmed.
          <br />
          <br />
          <sub>We emailed you the meeting link. If you didn't receive it, please contact us at: <a href='mailto:support@statusmoney.com'>support@statusmoney.com</a></sub>
        </PaneNote>
      </CardBlock>
      { flow !== 'regular' && (
        <div className={ styles.setupAccountLink }>
          <NavigationLink to='dashboard' onClick={ onClose }>Now let’s finish setting up <br />your account.</NavigationLink>
        </div>
      )}
    </AdvisorRequestPanePreset>
  )
}
